import { useState, useEffect } from "react";
import { EditButton } from "./EditButtons";
import { ProfileImage } from "./ProfileImage";
import { useAuth } from "../../contexts/AuthContext";
import {
  FirstNameSection,
  LastNameSection,
  ProfileLocationSection,
  BioSection,
} from "./FormSection";

export const EditProfileForm = () => {
  const { updateProfile, loading, currentUser } = useAuth();

  // formData.location holds either:
  //   - null (no location set)
  //   - { coordinates: [lng, lat], label, addressParts?, precision? }
  // The picker writes to it; we send it to the backend on submit.
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    location: null,
    bio: "",
  });

  // Track whether the user has changed their location during this edit session.
  // We only send the location field on submit if they touched it — that way
  // we don't accidentally re-geocode (and possibly re-label) an unchanged location.
  const [locationDirty, setLocationDirty] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Hydrate form from currentUser when it's available
  useEffect(() => {
    if (currentUser) {
      // currentUser.location is the new GeoJSON shape (or null)
      const userLocation = currentUser.location || null;

      setFormData({
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
        location: userLocation
          ? {
              coordinates: userLocation.coordinates,
              label: userLocation.label,
            }
          : null,
        bio: currentUser.bio || "",
      });
      setLocationDirty(false);
    }
  }, [currentUser]);

  const handleChange = (field) => (eOrValue) => {
    const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "location") {
      setLocationDirty(true);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    setErrors({});

    try {
      const dataToSubmit = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        bio: formData.bio,
      };

      // Only include location in the payload if the user actually changed it.
      // Backend understands three shapes for `location`:
      //   null                                                → clear it
      //   { coordinates, precision }                          → save these coords
      //   { searchQuery, countryCode }                        → forward-geocode then save
      if (locationDirty) {
        if (formData.location === null) {
          dataToSubmit.location = null;
        } else if (
          Array.isArray(formData.location.coordinates) &&
          formData.location.coordinates.length === 2
        ) {
          dataToSubmit.location = {
            coordinates: formData.location.coordinates,
            precision: formData.location.precision || "geocoded",
          };
        }
      }

      console.log("Submitting profile data:", dataToSubmit);

      const result = await updateProfile(dataToSubmit);

      setSubmitLoading(false);
      if (result.success) {
        setSuccessMessage("Profile updated successfully!");
        setLocationDirty(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(result.error || "Failed to update profile");
        console.error("Profile update failed:", result.error);
      }
    } catch (error) {
      setSubmitLoading(false);
      setErrorMessage("An error occurred while updating profile");
      console.error("Profile update error:", error);
    }
  };

  if (loading) {
    return <div>Loading profile...</div>;
  }

  if (!currentUser) {
    return <div>No user data found</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <ProfileImage />

      <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-600">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{errorMessage}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <FirstNameSection
            value={formData.firstName}
            onChange={handleChange("firstName")}
            error={errors.firstName}
          />

          <LastNameSection
            value={formData.lastName}
            onChange={handleChange("lastName")}
            error={errors.lastName}
          />
        </div>

        <ProfileLocationSection
          value={formData.location}
          onChange={handleChange("location")}
          error={errors.location}
        />

        <BioSection
          value={formData.bio}
          onChange={handleChange("bio")}
          error={errors.bio}
        />

        <EditButton
          onSubmit={handleSubmit}
          loading={submitLoading}
          success={successMessage}
        />
      </form>
    </div>
  );
};